// hooks/useLanguageAndCurrency.js
import { SAKA_COOKIE_NAMES } from "@/constants/cookieNames";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { usePathname, useRouter } from "@/i18n/navigation";
import { normalizeLocale } from "@/i18n/normalizeLocale";
import { routing } from "@/i18n/routing";
import { authService } from "@/services/authService";
import { getRouteDetails, stripAllLeadingLocales } from "@/utils/routeHelper";
import Cookies from "js-cookie";
import { useLocale } from "next-intl";
import {
  usePathname as useNextPathname,
  useParams,
  useSearchParams,
} from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

export const useLanguageAndCurrency = () => {
  const { currency } = useCurrency();
  const router = useRouter();
  const pathname = usePathname();
  const routeDetails = getRouteDetails(pathname);
  // routeDetails.path artık unknown route'larda null. Fallback'te raw pathname'i
  // strip ediyoruz; aksi halde "/de/foo" gibi locale-prefixed bir değer
  // router.replace'e raw href olarak gider ve next-intl yeni prefix ekleyerek
  // "/en/de/foo" bug'ını üretir.
  const navigationPathname =
    routeDetails.path || stripAllLeadingLocales(pathname) || "/";
  const params = useParams();
  const locale = useLocale();
  const nativePathname = useNextPathname();
  const searchParams = useSearchParams();
  const [selectedCurrency, setSelectedCurrency] = useState(currency);
  const [selectedLanguage, setSelectedLanguage] = useState(locale);
  const [loading, setLoading] = useState(false);
  const [waitingForRouteChange, setWaitingForRouteChange] = useState(false);
  const previousPathname = useRef(nativePathname);
  const reloadScheduled = useRef(false);
  const { errorModal } = useModalHelpers();
  const { session } = useAuth();
  const t = useTranslationDetector();

  // Idempotent reload helper. Both the route-change listener and the no-op
  // fallback timer share this so only one reload ever fires.
  const scheduleReload = useCallback((delay = 1000) => {
    if (reloadScheduled.current) return;
    reloadScheduled.current = true;
    setTimeout(() => {
      window.location.reload();
    }, delay);
  }, []);

  const getQueryParams = () => {
    const queryParams = {};
    searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });
    return queryParams;
  };

  const handleCurrencyChange = (newCurrency) => {
    setSelectedCurrency(newCurrency);
  };

  const handleLanguageChange = (newLanguage) => {
    setSelectedLanguage(newLanguage);
  };

  useEffect(() => {
    const cookieLocale = normalizeLocale(Cookies.get(SAKA_COOKIE_NAMES.LOCALE));
    if (cookieLocale && cookieLocale !== selectedLanguage) {
      setSelectedLanguage(cookieLocale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Route değişimini dinle
  useEffect(() => {
    if (waitingForRouteChange && nativePathname !== previousPathname.current) {
      scheduleReload();
    }
    previousPathname.current = nativePathname;
  }, [nativePathname, waitingForRouteChange, scheduleReload]);

  const applyChanges = async (onSuccess) => {
    setLoading(true);
    try {
      // Defansif normalize: API zaten lowercase dönüyor ama gelecekte
      // değişebilir veya cookie path'i farklı bir kaynaktan kirlenebilir.
      const normalizedLocale = String(selectedLanguage || "").toLowerCase();
      if (!routing.locales.includes(normalizedLocale)) {
        errorModal({
          message: t("common.error"),
          size: "sm",
          showCancel: false,
          isBottomSheet: true,
        });
        return;
      }

      const requestBody = {
        CurrencyCode: selectedCurrency,
        LanguageCode: normalizedLocale.toUpperCase(),
        UserId: session?.UserId,
      };

      const response = await authService.changeCurrencyAndLanguage(requestBody);

      if (!response?.IsOk) {
        errorModal({
          message:
            response?.Type && response?.Name
              ? t(`${response.Type}.${response.Name}`)
              : t("common.error"),
          size: "sm",
          showCancel: false,
          isBottomSheet: true,
        });
        return;
      }

      Cookies.set(SAKA_COOKIE_NAMES.LOCALE, normalizedLocale, {
        expires: 365,
        path: "/",
        sameSite: "lax",
      });
      Cookies.set(SAKA_COOKIE_NAMES.CURRENCY, selectedCurrency, {
        expires: 365,
        path: "/",
        sameSite: "lax",
      });

      const languageChanged = normalizedLocale !== locale;
      const currencyChanged = selectedCurrency !== currency;

      if (languageChanged) {
        const routeParams = { ...params };
        delete routeParams.locale;

        // Route-change fallback'ı her dil değişiminde aktive et — currency'ye
        // bağımlı olmamalı. Aksi halde no-op navigation veya AutoLogin race
        // durumlarında reload tetiklenmiyor.
        setWaitingForRouteChange(true);

        const query = getQueryParams();
        const hasParams = Object.keys(routeParams).length > 0;
        const hasQuery = Object.keys(query).length > 0;
        const href =
          hasParams || hasQuery
            ? {
                pathname: navigationPathname,
                ...(hasParams ? { params: routeParams } : {}),
                ...(hasQuery ? { query } : {}),
              }
            : navigationPathname;

        router.replace(href, { locale: normalizedLocale });

        // No-op safeguard: route gerçekten değişmezse (zaten doğru locale'de
        // olma, AutoLogin cookie override, ISG cache HIT) hard reload garanti.
        scheduleReload(1500);

        if (onSuccess) {
          onSuccess();
        }
      } else if (currencyChanged) {
        if (onSuccess) {
          onSuccess();
        }
        scheduleReload();
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      errorModal({
        message: error?.response?.data?.Message || t("common.error"),
        size: "sm",
        showCancel: false,
        isBottomSheet: true,
      });
      setWaitingForRouteChange(false);
      // Modal açık kalsın — kullanıcı retry yapabilsin (network hatası vb.)
    } finally {
      setLoading(false);
    }
  };

  const resetSelections = () => {
    setSelectedCurrency(currency);
    setSelectedLanguage(locale);
  };

  return {
    selectedCurrency,
    selectedLanguage,
    loading,
    handleCurrencyChange,
    handleLanguageChange,
    applyChanges,
    resetSelections,
  };
};
